/*
Toggle class on another element.

HTML Usage:
<toggle-element-class data-options='{}'>
  Toggle element class button
</toggle-element-class>
*/

if (!customElements.get('toggle-element-class')) {
  class ToggleElementClass extends HTMLElement {
    constructor() {
      super();

      // Options
      this.options = {
        toggleClass: 'toggle--is-active',
        toggleTriggerClass: '',
        target: 'body',
        onHover: false,
        hoverDelay: 0,
        preventDefault: true,
        stopPropagation: true,
        ariaExpanded: false,
        checkCollectionIntroText: false, // New option to enable collection intro text height check
      };

      // Get options from element data and combine with this.options
      if (this?.dataset?.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions,
        };
      }

      // Reduce actions (So the event can also be removed)
      this.reducer = {
        click: (e) => this.debounceClickEvent(e),
        mouseEnter: (e) => this.debouncedOnMouse(e, 'add'),
        mouseLeave: (e) => this.debouncedOnMouse(e, 'remove'),
      };

      // Construct webcomponent
      this.construct();
    }

    /*
      Construct webcomponent
    */
    construct() {
      this.target = document.querySelector(this.options.target);

      if (!this.target) {
        return false;
      }

      // Check collection intro text height if enabled
      if (this.options.checkCollectionIntroText) {
        this.checkCollectionIntroTextHeight();

        // Add resize listener to re-check on window resize
        this.resizeHandler = () => this.checkCollectionIntroTextHeight();
        window.addEventListener('resize', this.resizeHandler);
      }

      // Click
      this.removeEventListener('click', this.reducer.click);
      this.addEventListener('click', this.reducer.click);

      // Mouse enter and leave
      if (this.options.onHover) {
        this.removeEventListener('mouseenter', this.reducer.mouseEnter);
        this.addEventListener('mouseleave', this.reducer.mouseLeave);
      }
    }

    /*
      Check if .collection-intro__text elements have p elements with more height than the container
      due to line-clamp-2 truncation
    */
    checkCollectionIntroTextHeight() {
      const collectionIntroTexts = document.querySelectorAll('.collection-intro__text');
      console.log('Found collection intro texts:', collectionIntroTexts.length);

      collectionIntroTexts.forEach((container, index) => {
        const pElement = container.querySelector('p');
        console.log(`Container ${index}:`, container);

        if (pElement) {
          // Find the toggle button (sibling of the container's parent)
          const toggleButton = container
            .closest('.collection-intro__text-wrapper')
            ?.querySelector('.collection-intro__toggle');

          console.log(`Toggle button found:`, toggleButton);

          // Get the computed styles to check for line-clamp
          const computedStyle = window.getComputedStyle(container);
          const lineClamp = computedStyle.getPropertyValue('-webkit-line-clamp');

          // Check if line-clamp is applied (line-clamp-2) or if the container has the class
          const hasLineClamp =
            (lineClamp && lineClamp !== 'none') || container.classList.contains('line-clamp-2');

          console.log(
            `Line-clamp detected:`,
            hasLineClamp,
            `Line-clamp value:`,
            lineClamp,
            `Has class:`,
            container.classList.contains('line-clamp-2')
          );

          // Always measure heights to determine if content is truncated
          // Create a temporary clone to measure full content height
          const tempClone = container.cloneNode(true);

          // Remove all classes and set a unique class to avoid CSS inheritance
          tempClone.className = 'temp-clone-for-measurement';

          // Set all styles to ensure no line-clamp restrictions
          tempClone.style.position = 'absolute';
          tempClone.style.visibility = 'hidden';
          tempClone.style.height = 'auto';
          tempClone.style.maxHeight = 'none';
          tempClone.style.webkitLineClamp = 'none';
          tempClone.style.webkitBoxOrient = 'unset';
          tempClone.style.display = 'block';
          tempClone.style.overflow = 'visible';
          tempClone.style.textOverflow = 'clip';
          tempClone.style.whiteSpace = 'normal';
          tempClone.style.lineHeight = 'normal';
          tempClone.style.fontSize = window.getComputedStyle(container).fontSize;
          tempClone.style.fontFamily = window.getComputedStyle(container).fontFamily;
          tempClone.style.width = window.getComputedStyle(container).width;

          // Add to DOM temporarily to measure
          document.body.appendChild(tempClone);

          const fullContentHeight = tempClone.offsetHeight;
          const containerHeight = container.offsetHeight;

          console.log(`Heights - Full: ${fullContentHeight}, Container: ${containerHeight}`);
          console.log(`Clone element:`, tempClone);
          console.log(`Clone computed styles:`, {
            webkitLineClamp: window.getComputedStyle(tempClone).webkitLineClamp,
            webkitBoxOrient: window.getComputedStyle(tempClone).webkitBoxOrient,
            display: window.getComputedStyle(tempClone).display,
            overflow: window.getComputedStyle(tempClone).overflow,
            height: window.getComputedStyle(tempClone).height,
            maxHeight: window.getComputedStyle(tempClone).maxHeight,
          });

          // Remove temporary clone
          document.body.removeChild(tempClone);

          // If full content height is greater than container height, content is truncated
          if (fullContentHeight > containerHeight) {
            console.log(`Content is TRUNCATED - showing toggle`);
            // Add a class or data attribute to indicate truncation
            container.classList.add('has-truncated-content');
            container.setAttribute('data-content-truncated', 'true');
            container.setAttribute('data-full-height', fullContentHeight);
            container.setAttribute('data-container-height', containerHeight);

            // Show the toggle button if content is truncated
            if (toggleButton) {
              toggleButton.style.display = 'inline-block';
              console.log(`Toggle button display set to:`, toggleButton.style.display);
            }

            // You can also emit a custom event if needed
            const event = new CustomEvent('contentTruncated', {
              detail: {
                container: container,
                pElement: pElement,
                fullHeight: fullContentHeight,
                containerHeight: containerHeight,
              },
            });
            document.dispatchEvent(event);
          } else {
            console.log(`Content is NOT truncated - hiding toggle`);
            // Remove classes if content is not truncated
            container.classList.remove('has-truncated-content');
            container.removeAttribute('data-content-truncated');
            container.removeAttribute('data-full-height');
            container.removeAttribute('data-container-height');

            // Hide the toggle button if content is not truncated
            if (toggleButton) {
              toggleButton.style.display = 'none';
              console.log(`Toggle button display set to:`, toggleButton.style.display);
            }
          }
        } else {
          console.log(`No p element found in container ${index}`);
        }
      });
    }

    /*
      Debounce click event
      @param e {object}: mouseevent
      Called by add event listener -> reducer.
    */
    debounceClickEvent(e) {
      if (!e) {
        return false;
      }
      this.preventDefault(e);
      this.stopPropagation(e);
      this.toggle();
    }

    /*
      Debounced mouse event
      @param e {object}: mouseevent
      @param type {string}: open/close/closeAll
      Called by add event listener -> reducer.
    */
    debouncedOnMouse(e, type) {
      if (!e) {
        return false;
      }
      const debouncer = debounce((type) => {
        switch (type) {
          case 'add':
            this.add();
            break;
          case 'remove':
            this.remove();
            break;
          default:
            this.toggle();
            break;
        }
      }, this.options.hoverDelay);
      debouncer(type);
    }

    // @param e {object}: click event
    preventDefault(e) {
      if (this.options.preventDefault) {
        e.preventDefault();
      }
    }

    // @param e {object}: click event
    stopPropagation(e) {
      if (this.options.stopPropagation) {
        e.stopPropagation();
      }
    }

    /*
      Toggle text switching between "Read more" and "Show less"
    */
    toggleText() {
      const toggleTexts = this.querySelectorAll('[class*="toggle-text"]');
      toggleTexts.forEach((text) => {
        text.classList.toggle('hidden');
      });
    }

    add() {
      const { toggleClass, toggleTriggerClass } = this.options;
      if (this.target) {
        this.target.classList.add(toggleClass);
      }
      if (toggleTriggerClass) {
        this.classList.add(toggleTriggerClass);
      }
      // Switch aria-expanded
      if (this.options.ariaExpanded) {
        this.setAttribute('aria-expanded', true);
      }
      // Toggle text switching
      this.toggleText();
    }

    remove() {
      const { toggleClass, toggleTriggerClass } = this.options;
      if (this.target) {
        this.target.classList.remove(toggleClass);
      }
      if (toggleTriggerClass) {
        this.classList.remove(toggleTriggerClass);
      }
      // Switch aria-expanded
      if (this.options.ariaExpanded) {
        this.setAttribute('aria-expanded', false);
      }
      // Toggle text switching
      this.toggleText();
    }

    toggle() {
      const { toggleClass } = this.options;
      if (!this.target) {
        return false;
      }
      if (this.options && this.options.removeClasses) {
        if (this.target.classList.contains(toggleClass)) {
          return;
        }
        let that = this;
        let removeClasses = this.options.removeClasses.split(',');
        removeClasses.forEach((className) => {
          that.target.classList.remove(className);
        });
      }
      if (!this.target.classList.contains(toggleClass)) {
        this.add();
      } else {
        this.remove();
      }
    }
  }

  window.ToggleElementClass = ToggleElementClass;

  customElements.define('toggle-element-class', ToggleElementClass);
}
